export type AppLocale = "sr" | "sr_cyr" | "en";

export const LOCALES: AppLocale[] = ["sr", "sr_cyr", "en"];
export const DEFAULT_LOCALE: AppLocale = "sr";

/**
 * Valid BCP-47. Bare "sr" is script-ambiguous and "sr_cyr" is not a language tag
 * at all — Google silently discards the whole annotation when it is invalid,
 * which is why the language cluster never formed.
 */
export const BCP47: Record<AppLocale, string> = {
  sr: "sr-Latn",
  sr_cyr: "sr-Cyrl",
  en: "en",
};

/** Open Graph wants language_TERRITORY, not BCP-47. */
export const OG_LOCALE: Record<AppLocale, string> = {
  sr: "sr_RS",
  sr_cyr: "sr_RS",
  en: "en_US",
};

/** WordPress language code -> URL locale segment. Mirrors trRouteSegmentMap. */
export const WP_LOCALE: Record<string, AppLocale> = {
  RS: "sr",
  EN: "en",
  SR: "sr_cyr",
};

export const SITE_URL = (process.env.GATSBY_APP_URL || "").replace(/\/+$/, "");

export const localeFromPathname = (pathname?: string): AppLocale => {
  const segment = (pathname || "/").split("/")[1];
  return (LOCALES as string[]).includes(segment)
    ? (segment as AppLocale)
    : DEFAULT_LOCALE;
};

/** `<html lang>` value for a given path. */
export const htmlLang = (pathname?: string): string =>
  BCP47[localeFromPathname(pathname)];

/** "a/b" | "/a/b" | "/a/b/" -> "/a/b/" ; "" | "/" -> "/"  (trailingSlash: always) */
export const withTrailingSlash = (path: string): string => {
  const clean = (path || "").replace(/^\/+/, "").replace(/\/+$/, "");
  return clean === "" ? "/" : `/${clean}/`;
};

/**
 * Absolute, locale-prefixed, trailing-slashed. Canonicals are ALWAYS built this
 * way, which is what consolidates the unprefixed duplicates (`/kalendar/`) onto
 * their `/sr/` twin without needing a redirect.
 */
export const localeUrl = (locale: AppLocale, path: string): string =>
  `${SITE_URL}${withTrailingSlash(`${locale}${withTrailingSlash(path)}`)}`;
